import { Material, Object3D, Texture } from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

import { WebArError, type WebArLaunchPayload } from "./contracts.js";
import type { VerifiedWebArModel, WebArModelPort } from "./controller.js";
import type { Xr8SceneModel } from "./xr8-types.js";

export interface GlbParserPort {
  parse(bytes: ArrayBuffer): Promise<Xr8SceneModel>;
}

export interface VerifiedGlbModelLoaderOptions {
  readonly fetch: typeof fetch;
  readonly digest: (bytes: ArrayBuffer) => Promise<string>;
  readonly parser: GlbParserPort;
}

const GLB_MAGIC = 0x46546c67;
const GLB_JSON_CHUNK = 0x4e4f534a;

function assertNoExternalUris(value: unknown, path = "glTF"): void {
  if (Array.isArray(value)) {
    value.forEach((entry, index) => assertNoExternalUris(entry, `${path}[${index}]`));
    return;
  }
  if (typeof value !== "object" || value === null) return;
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (key.toLowerCase() === "uri") {
      throw new WebArError(
        "model_integrity_failed",
        `The committed GLB contains an external resource at ${path}.${key}.`,
        false
      );
    }
    assertNoExternalUris(entry, `${path}.${key}`);
  }
}

export function assertSelfContainedGlb(bytes: ArrayBuffer): void {
  if (bytes.byteLength < 20) {
    throw new WebArError("model_integrity_failed", "The GLB is truncated.", false);
  }
  const view = new DataView(bytes);
  if (view.getUint32(0, true) !== GLB_MAGIC || view.getUint32(4, true) !== 2) {
    throw new WebArError("model_integrity_failed", "The model is not a GLB 2.0 file.", false);
  }
  if (view.getUint32(8, true) !== bytes.byteLength) {
    throw new WebArError("model_integrity_failed", "The GLB length is inconsistent.", false);
  }
  const jsonLength = view.getUint32(12, true);
  if (
    view.getUint32(16, true) !== GLB_JSON_CHUNK ||
    jsonLength <= 0 ||
    20 + jsonLength > bytes.byteLength
  ) {
    throw new WebArError("model_integrity_failed", "The GLB JSON chunk is invalid.", false);
  }
  let document: unknown;
  try {
    const json = new TextDecoder("utf-8", { fatal: true })
      .decode(new Uint8Array(bytes, 20, jsonLength))
      .replace(/[\u0000\u0020]+$/u, "");
    document = JSON.parse(json);
  } catch (cause) {
    throw new WebArError("model_integrity_failed", "The GLB JSON is invalid.", false, {
      cause
    });
  }
  assertNoExternalUris(document);
}

function disposeObject(root: Object3D): void {
  root.traverse((object) => {
    const renderable = object as Object3D & {
      geometry?: { dispose(): void };
      material?: Material | Material[];
    };
    renderable.geometry?.dispose();
    const materials = Array.isArray(renderable.material)
      ? renderable.material
      : renderable.material ? [renderable.material] : [];
    for (const material of materials) {
      for (const value of Object.values(material)) {
        if (value instanceof Texture) value.dispose();
      }
      material.dispose();
    }
  });
}

function defaultGlbParser(): GlbParserPort {
  const loader = new GLTFLoader();
  return {
    parse(bytes) {
      return new Promise((resolve, reject) => {
        loader.parse(bytes, "", resolve, reject);
      });
    }
  };
}

export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export function createVerifiedGlbModelLoader(
  options: Partial<VerifiedGlbModelLoaderOptions> = {}
): WebArModelPort {
  const fetchPort = options.fetch ?? fetch;
  const digest = options.digest ?? sha256Hex;
  const parser = options.parser ?? defaultGlbParser();
  return {
    async load(model: WebArLaunchPayload["model"], signal): Promise<VerifiedWebArModel> {
      let response: Response;
      try {
        response = await fetchPort(model.url, {
          method: "GET",
          credentials: "omit",
          redirect: "error",
          cache: "no-store",
          referrerPolicy: "no-referrer",
          headers: { accept: "model/gltf-binary" },
          signal
        });
      } catch (cause) {
        if (signal.aborted) {
          throw new WebArError("cancelled", "Model loading was cancelled.", true, { cause });
        }
        throw new WebArError("model_load_failed", "The model could not be downloaded.", true, {
          cause
        });
      }
      const mediaType = response.headers.get("content-type")?.split(";", 1)[0]?.trim().toLowerCase();
      const contentLengthHeader = response.headers.get("content-length");
      const contentLength = contentLengthHeader === null ? undefined : Number(contentLengthHeader);
      if (
        !response.ok ||
        mediaType !== model.mediaType ||
        response.url !== model.url ||
        (contentLength !== undefined && contentLength !== model.byteLength)
      ) {
        throw new WebArError("model_load_failed", "The model response did not match its grant.", false);
      }

      const bytes = await response.arrayBuffer();
      if (signal.aborted) {
        throw new WebArError("cancelled", "Model loading was cancelled.", true);
      }
      if (bytes.byteLength !== model.byteLength) {
        throw new WebArError("model_integrity_failed", "The model byte length did not match.", false);
      }
      const actualHash = await digest(bytes);
      if (actualHash !== model.sha256) {
        throw new WebArError("model_integrity_failed", "The model checksum did not match.", false);
      }
      assertSelfContainedGlb(bytes);

      let parsed: Xr8SceneModel;
      try {
        parsed = await parser.parse(bytes);
      } catch (cause) {
        throw new WebArError("model_load_failed", "The verified GLB could not be decoded.", false, {
          cause
        });
      }
      let disposed = false;
      return {
        resource: parsed,
        sha256: actualHash,
        byteLength: bytes.byteLength,
        dispose() {
          if (disposed) return;
          disposed = true;
          if (parsed.scene instanceof Object3D) disposeObject(parsed.scene);
        }
      };
    }
  };
}
